import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { lawApi } from '../../lawApi';
import { useTheme } from '../../components/LawPortalLayout';
import {
  User, GraduationCap, FileText, BookOpen, AlertTriangle,
  ChevronRight, Clock, ArrowRight, CheckCircle2, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const URGENCY_STYLES = { high: 'bg-red-500', medium: 'bg-amber-500', low: 'bg-blue-500' };

function StatCard({ label, value, sub, icon: Icon, link, theme }) {
  const { dark, textPrimary, textSecondary, borderColor } = theme;
  const cardBg = dark ? 'bg-[#141618]' : 'bg-white';
  return (
    <Link to={link} className={`${cardBg} border ${borderColor} rounded-lg p-4 hover:shadow-sm transition-shadow block`}>
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-[11px] uppercase tracking-wide ${textSecondary}`}>{label}</p>
          <p className={`text-2xl font-semibold mt-1 ${textPrimary}`} style={{ fontFamily: "'Inter', sans-serif" }}>{value}</p>
          {sub && <p className={`text-[11px] mt-0.5 ${textSecondary}`}>{sub}</p>}
        </div>
        {Icon && <Icon className={`h-4 w-4 ${textSecondary}`} strokeWidth={1.5} />}
      </div>
    </Link>
  );
}

export default function LawDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const { dark, textPrimary, textSecondary, textMuted, borderColor, hoverBg } = theme;
  const cardBg = dark ? 'bg-[#141618]' : 'bg-white';

  useEffect(() => {
    lawApi.getDashboard()
      .then(setData)
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-4 animate-pulse">
      <div className={`h-24 ${dark ? 'bg-[#1C1E20]' : 'bg-[#E8E6E1]'} rounded-lg`} />
      <div className="grid grid-cols-4 gap-3">
        {[1,2,3,4].map(i => <div key={i} className={`h-20 ${dark ? 'bg-[#1C1E20]' : 'bg-[#E8E6E1]'} rounded-lg`} />)}
      </div>
    </div>
  );

  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="law-dashboard">
      {/* Welcome Block */}
      <div className={`${cardBg} border ${borderColor} rounded-lg p-5`}>
        <div className="flex items-start justify-between">
          <div>
            <p className={`text-[11px] uppercase tracking-wider ${textMuted}`}>Welcome back</p>
            <h1 className={`text-xl font-semibold mt-0.5 ${textPrimary}`} style={{ fontFamily: "'Inter', sans-serif" }}
              data-testid="law-welcome-name">{data.student_name}</h1>
            <div className={`flex items-center gap-4 mt-2 text-[11px] ${textSecondary}`}>
              <span>Advisor: <strong className={textPrimary}>{data.assigned_advisor}</strong></span>
              <span className={`w-px h-3 ${dark ? 'bg-[#333]' : 'bg-[#DDD]'}`} />
              <span>{data.program_tier}</span>
              <span className={`w-px h-3 ${dark ? 'bg-[#333]' : 'bg-[#DDD]'}`} />
              <span>Cycle {data.cycle_year}</span>
            </div>
          </div>
          <div className={`text-right`}>
            <p className={`text-[10px] uppercase tracking-wider ${textMuted}`}>Profile</p>
            <p className={`text-lg font-semibold ${data.profile_completeness >= 70 ? 'text-emerald-500' : data.profile_completeness >= 40 ? 'text-amber-500' : 'text-red-400'}`}>
              {data.profile_completeness}%
            </p>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {data.alerts.length > 0 && (
        <div className="space-y-1.5" data-testid="law-alerts">
          {data.alerts.map((alert, i) => (
            <Link key={`alert-${alert.link}-${i}`} to={alert.link}
              className={`flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-xs ${alert.type === 'error' ? (dark ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-700') : (dark ? 'bg-amber-900/20 text-amber-400' : 'bg-amber-50 text-amber-700')}`}>
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
              {alert.message}
              <ChevronRight className="h-3 w-3 ml-auto" />
            </Link>
          ))}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="law-stats">
        <StatCard label="Schools" value={data.schools_count} icon={GraduationCap}
          sub={`${data.schools_by_category.reach}R · ${data.schools_by_category.target}T · ${data.schools_by_category.likely}L`}
          link="/law-portal/schools" theme={theme} />
        <StatCard label="Applications" value={data.applications_total} icon={FileText}
          sub={data.applications_total > 0 ? `${data.applications.submitted || 0} submitted` : 'None started'}
          link="/law-portal/applications" theme={theme} />
        <StatCard label="Essays" value={data.essays_total} icon={BookOpen}
          sub={data.essays_total > 0 ? `${data.essays.final || 0} final, ${data.essays.drafting || 0} drafting` : 'None created'}
          link="/law-portal/essays" theme={theme} />
        <StatCard label="LSAT" value={data.lsat_score || '—'} icon={TrendingUp}
          sub={data.lsat_score ? `Target: ${data.lsat_target || '—'}` : (data.testing_status === 'in_progress' ? 'Preparing' : 'Not started')}
          link="/law-portal/profile" theme={theme} />
      </div>

      {/* Priorities + Deadlines */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Priorities */}
        <div className={`${cardBg} border ${borderColor} rounded-lg`} data-testid="law-priorities">
          <div className={`px-4 py-3 border-b ${borderColor}`}>
            <h2 className={`text-[11px] font-semibold uppercase tracking-wider ${textSecondary}`}>Next Priorities</h2>
          </div>
          <div className="divide-y divide-inherit">
            {data.priorities.length === 0 ? (
              <div className="p-4">
                <p className={`text-xs ${textMuted}`}>No priorities — you're on track</p>
              </div>
            ) : data.priorities.map((p, i) => (
              <Link key={`priority-${p.link}-${i}`} to={p.link}
                className={`flex items-center gap-3 px-4 py-3 ${hoverBg} transition-colors`}>
                <div className={`w-1.5 h-1.5 rounded-full ${URGENCY_STYLES[p.urgency] || 'bg-gray-400'}`} />
                <span className={`text-xs flex-1 ${textPrimary}`}>{p.action}</span>
                <ArrowRight className={`h-3 w-3 ${textMuted}`} />
              </Link>
            ))}
          </div>
        </div>

        {/* Upcoming Deadlines */}
        <div className={`${cardBg} border ${borderColor} rounded-lg`} data-testid="law-deadlines">
          <div className={`px-4 py-3 border-b ${borderColor}`}>
            <h2 className={`text-[11px] font-semibold uppercase tracking-wider ${textSecondary}`}>Upcoming Deadlines</h2>
          </div>
          <div className="divide-y divide-inherit">
            {data.upcoming_deadlines.length === 0 ? (
              <div className="p-4">
                <p className={`text-xs ${textMuted}`}>No deadlines — add schools to see dates</p>
              </div>
            ) : data.upcoming_deadlines.map((d, i) => (
              <div key={`deadline-${d.name}-${d.deadline}-${i}`} className={`flex items-center gap-3 px-4 py-3`}>
                <Clock className={`h-3.5 w-3.5 ${textMuted}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium truncate ${textPrimary}`}>{d.name}</p>
                  <p className={`text-[10px] ${textMuted} capitalize`}>{d.category}</p>
                </div>
                <span className={`text-xs font-mono ${textSecondary}`}>{d.deadline}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Essay Progress */}
      {data.essays_total > 0 && (
        <div className={`${cardBg} border ${borderColor} rounded-lg p-4`} data-testid="law-essay-progress">
          <h2 className={`text-[11px] font-semibold uppercase tracking-wider ${textSecondary} mb-3`}>Essay Progress</h2>
          <div className="flex gap-1.5 h-2 rounded-full overflow-hidden">
            {Object.entries(data.essays).filter(([_, v]) => v > 0).map(([status, count]) => {
              const pct = (count / data.essays_total) * 100;
              const colors = {
                submitted: 'bg-emerald-500', final: 'bg-emerald-400',
                advisor_review: 'bg-blue-500', revision: 'bg-amber-500',
                drafting: 'bg-amber-300', not_started: dark ? 'bg-[#333]' : 'bg-[#DDD]'
              };
              return <div key={status} className={`${colors[status] || 'bg-gray-300'} rounded-full`} style={{ width: `${pct}%` }} title={`${status}: ${count}`} />;
            })}
          </div>
          <div className={`flex gap-4 mt-2 text-[10px] ${textMuted}`}>
            {Object.entries(data.essays).filter(([_, v]) => v > 0).map(([status, count]) => (
              <span key={status} className="capitalize">{status.replace('_', ' ')}: {count}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
